/**
 * Histogram utility functions for real-time histogram calculation and rendering
 * Feature: 012-histogram-display
 */

import { HistogramData, HistogramMode, ExifDisplayData, ExifData } from '../../types/histogram';
import { normalizeExifData, formatISO, formatFocalLength, formatAperture, formatShutterSpeed } from '@/utils/exif';

/**
 * Calculate histogram from image data (CPU-based implementation)
 *
 * @param imageData - ImageData from canvas context (RGBA pixel data)
 * @returns HistogramData with normalized bin values (0-255) for all channels
 *
 * Performance target: <10ms for 1920×1080 images
 */
export function calculateHistogram(imageData: ImageData): HistogramData {
  const { data, width, height } = imageData;
  const totalPixels = width * height;

  // Initialize raw histogram bins (256 bins per channel) - using Uint32Array for raw counts
  const redRaw = new Uint32Array(256);
  const greenRaw = new Uint32Array(256);
  const blueRaw = new Uint32Array(256);
  const luminanceRaw = new Uint32Array(256);

  // Rec. 709 luma coefficients for luminance calculation.
  // These specific values (0.2126, 0.7152, 0.0722) are defined by the ITU-R Recommendation BT.709 standard
  // and are the industry standard for HDTV luminance calculation. They account for the human eye's
  // varying sensitivity to different colors (most sensitive to green, least to blue).
  const R_LUMA = 0.2126;
  const G_LUMA = 0.7152;
  const B_LUMA = 0.0722;

  // Iterate through pixels (data is RGBA format: [R, G, B, A, R, G, B, A, ...])
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // Alpha channel at data[i + 3] is ignored for histogram

    // Increment bin counts for each channel
    redRaw[r]++;
    greenRaw[g]++;
    blueRaw[b]++;

    // Calculate luminance (weighted average) and clamp to 0-255
    const lum = Math.round(R_LUMA * r + G_LUMA * g + B_LUMA * b);
    const lumClamped = Math.max(0, Math.min(255, lum));
    luminanceRaw[lumClamped]++;
  }

  // Find maximum bin value for each channel independently (FR-006a)
  let maxRedValue = 0;
  let maxGreenValue = 0;
  let maxBlueValue = 0;
  let maxLuminanceValue = 0;

  for (let i = 0; i < 256; i++) {
    maxRedValue = Math.max(maxRedValue, redRaw[i]);
    maxGreenValue = Math.max(maxGreenValue, greenRaw[i]);
    maxBlueValue = Math.max(maxBlueValue, blueRaw[i]);
    maxLuminanceValue = Math.max(maxLuminanceValue, luminanceRaw[i]);
  }

  // Store the global maximum for reference (highest across all channels)
  const maxBinValue = Math.max(maxRedValue, maxGreenValue, maxBlueValue, maxLuminanceValue);

  // Normalize histogram values to 0-255 range per channel (FR-006a)
  // Each channel is normalized independently to use full dynamic range
  // Formula: normalized_value = (raw_count / channel_max_count) * 255
  const red = new Uint8Array(256);
  const green = new Uint8Array(256);
  const blue = new Uint8Array(256);
  const luminance = new Uint8Array(256);

  if (maxRedValue > 0) {
    for (let i = 0; i < 256; i++) {
      red[i] = Math.round((redRaw[i] / maxRedValue) * 255);
    }
  }

  if (maxGreenValue > 0) {
    for (let i = 0; i < 256; i++) {
      green[i] = Math.round((greenRaw[i] / maxGreenValue) * 255);
    }
  }

  if (maxBlueValue > 0) {
    for (let i = 0; i < 256; i++) {
      blue[i] = Math.round((blueRaw[i] / maxBlueValue) * 255);
    }
  }

  if (maxLuminanceValue > 0) {
    for (let i = 0; i < 256; i++) {
      luminance[i] = Math.round((luminanceRaw[i] / maxLuminanceValue) * 255);
    }
  }

  return {
    red,
    green,
    blue,
    luminance,
    totalPixels,
    maxBinValue, // Store the global raw max value before normalization for reference
    calculatedAt: Date.now(),
  };
}

/**
 * Render histogram to canvas using Canvas 2D API
 *
 * @param ctx - Canvas 2D rendering context
 * @param histogramData - Histogram bin data
 * @param mode - Display mode ('luminance' or 'rgb')
 * @param width - Canvas width in pixels
 * @param height - Canvas height in pixels
 *
 * Performance target: <5ms for 256-bin histogram
 */
export function renderHistogram(
  ctx: CanvasRenderingContext2D,
  histogramData: HistogramData,
  mode: HistogramMode,
  width: number,
  height: number
): void {
  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  const binWidth = width / 256;
  const { red, green, blue, luminance, maxBinValue } = histogramData;

  // Avoid rendering if histogram is empty
  if (maxBinValue === 0) {
    return;
  }

  if (mode === 'luminance') {
    // Luminance-only mode: single channel (white/gray)
    ctx.fillStyle = 'rgba(200, 200, 200, 0.8)';
    renderChannel(ctx, luminance, binWidth, height);
  } else {
    // RGB mode: additive blending for overlaid channels
    ctx.globalCompositeOperation = 'lighten';

    // Red channel
    ctx.fillStyle = 'rgba(255, 0, 0, 0.6)';
    renderChannel(ctx, red, binWidth, height);

    // Green channel
    ctx.fillStyle = 'rgba(0, 255, 0, 0.6)';
    renderChannel(ctx, green, binWidth, height);

    // Blue channel
    ctx.fillStyle = 'rgba(0, 0, 255, 0.6)';
    renderChannel(ctx, blue, binWidth, height);

    // Luminance baseline (lower opacity to avoid overwhelming color channels)
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    renderChannel(ctx, luminance, binWidth, height);

    // Reset composite operation
    ctx.globalCompositeOperation = 'source-over';
  }
}

/**
 * Render a single histogram channel as vertical bars
 *
 * @param ctx - Canvas 2D rendering context
 * @param bins - Normalized histogram bin values (256 values, range 0-255)
 * @param binWidth - Width of each bin in pixels
 * @param height - Canvas height in pixels
 */
function renderChannel(
  ctx: CanvasRenderingContext2D,
  bins: Uint8Array,
  binWidth: number,
  height: number
): void {
  for (let i = 0; i < 256; i++) {
    // Scale normalized bin value (0-255) to canvas height
    // bins[i] is already normalized: 255 = tallest bar, 0 = empty
    const barHeight = (bins[i] / 255) * height;

    // Draw bar from bottom up (y coordinate grows downward in canvas)
    const x = i * binWidth;
    const y = height - barHeight;

    ctx.fillRect(x, y, binWidth, barHeight);
  }
}

/**
 * Format EXIF data for display in histogram panel
 *
 * @param exifData - Raw EXIF data from photo metadata (feature 003)
 * @returns Formatted EXIF display data with proper units and labels
 */
export function formatExifForDisplay(exifData?: ExifData | any): ExifDisplayData {
  if (!exifData) {
    return {};
  }

  // Normalize to standard format first (handles both nested and flat structures)
  const normalized = normalizeExifData(exifData);
  if (!normalized) {
    return {};
  }

  // Use unified formatters for consistent formatting
  return {
    iso: formatISO(normalized) ?? undefined,
    focalLength: formatFocalLength(normalized) ?? undefined,
    aperture: formatAperture(normalized) ?? undefined,
    shutterSpeed: formatShutterSpeed(normalized) ?? undefined,
  };
}

/**
 * Validate histogram data integrity
 * Throws error if histogram data is invalid
 *
 * @param data - Histogram data to validate
 */
export function validateHistogramData(data: HistogramData): void {
  // Check array lengths
  if (data.red.length !== 256) {
    throw new Error('Invalid red histogram length');
  }
  if (data.green.length !== 256) {
    throw new Error('Invalid green histogram length');
  }
  if (data.blue.length !== 256) {
    throw new Error('Invalid blue histogram length');
  }
  if (data.luminance.length !== 256) {
    throw new Error('Invalid luminance histogram length');
  }

  // Verify normalized values are in valid range (0-255)
  const checkRange = (arr: Uint8Array, name: string) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] < 0 || arr[i] > 255) {
        throw new Error(`${name} histogram has out-of-range value at bin ${i}: ${arr[i]}`);
      }
    }
  };

  checkRange(data.red, 'Red');
  checkRange(data.green, 'Green');
  checkRange(data.blue, 'Blue');
  checkRange(data.luminance, 'Luminance');

  // If histogram is non-empty, verify at least one channel has max normalized value (255)
  if (data.maxBinValue > 0) {
    const hasMax255 =
      data.red.some((v) => v === 255) ||
      data.green.some((v) => v === 255) ||
      data.blue.some((v) => v === 255) ||
      data.luminance.some((v) => v === 255);

    if (!hasMax255) {
      throw new Error('Non-empty histogram missing max normalized value (255)');
    }
  }
}
